import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { articles } from '@/mocks/articles';

export default function Article() {
  const { id } = useParams<{ id: string }>();
  const article = articles.find((a) => a.id === Number(id));

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="font-heading text-2xl font-bold text-foreground-950 mb-2">文章未找到</h1>
            <Link to="/" className="text-primary-500 hover:text-primary-600 transition-colors cursor-pointer text-sm">返回首页</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: { '@type': 'Person', name: article.author },
    datePublished: article.publishDate,
    image: article.image,
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: typeof window !== 'undefined' ? window.location.origin : '' },
        { '@type': 'ListItem', position: 2, name: '文章列表' },
        { '@type': 'ListItem', position: 3, name: article.title },
      ],
    },
  };

  const relatedArticles = articles.filter((a) => a.id !== article.id).slice(0, 3);

  return (
    <>
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      <Navbar />
      <main className="pt-16">
        <Breadcrumb
          items={[
            { label: '首页', to: '/' },
            { label: '文章列表' },
            { label: article.title },
          ]}
        />

        <article className="w-full px-4 md:px-6 py-8 md:py-12">
          <div className="max-w-3xl mx-auto">
            <div className="mb-6">
              <span className="inline-block px-3 py-1 bg-primary-100 text-primary-700 text-xs font-medium rounded-full mb-4">
                {article.category}
              </span>
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4 leading-tight">
                {article.title}
              </h1>
              <div className="flex items-center gap-4 text-sm text-foreground-500">
                <span>{article.author}</span>
                <span>{article.publishDate}</span>
                <span className="flex items-center gap-1">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-eye-line"></i>
                  </div>
                  {article.readCount} 阅读
                </span>
              </div>
            </div>

            <div className="w-full aspect-[2/1] rounded-lg overflow-hidden mb-8">
              <img
                src={article.image}
                alt={article.title}
                className="w-full h-full object-cover"
                title={`${article.title} - 黄油网页游戏合集`}
              />
            </div>

            <div
              className="prose prose-sm md:prose-base max-w-none text-foreground-700 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="mt-12 pt-8 border-t border-background-200/70">
              <h3 className="font-heading text-lg font-semibold text-foreground-950 mb-5">相关文章</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {relatedArticles.map((related) => (
                  <Link
                    key={related.id}
                    to={`/article/${related.id}`}
                    className="group block bg-background-100 rounded-lg p-4 border border-background-200/70 hover:border-primary-300 transition-all duration-300 cursor-pointer"
                  >
                    <span className="text-xs text-primary-600 font-medium">{related.category}</span>
                    <h4 className="font-heading text-sm font-semibold text-foreground-900 mt-1.5 group-hover:text-primary-500 transition-colors line-clamp-2">
                      {related.title}
                    </h4>
                    <p className="text-xs text-foreground-500 mt-2 line-clamp-2">{related.excerpt}</p>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}